/**
 * Grouper Structure Utilities (new format only)
 * Ensures grouper rows strictly follow the schema defined in project structure.
 */

function assertCondition(condition: boolean, message: string): void {
  if (!condition) {
    throw new Error(`[grouper] ${message}`);
  }
}

function normalizeLevelId(level: any, index: number): string {
  if (!level) {
    return String(index + 1);
  }
  if (typeof level === 'string' || typeof level === 'number') {
    return String(level);
  }
  if (level.level !== undefined && level.level !== null) {
    return String(level.level);
  }
  return String(index + 1);
}

function getReferenceSources(levelStructure: any): any[] {
  if (!levelStructure?.sources || !Array.isArray(levelStructure.sources)) {
    return [];
  }
  return levelStructure.sources.filter((source: any) => source?.type === 'reference');
}

export function ensureGrouperStructureExists(grouperStructure: any, topicId: string): void {
  assertCondition(
    grouperStructure && Array.isArray(grouperStructure.levels) && grouperStructure.levels.length > 0,
    `Topic "${topicId}" is missing grouper levels in project structure`
  );
}

export function ensureGrouperArray(grouperData: any, topicId: string): any[] {
  assertCondition(
    Array.isArray(grouperData),
    'Topic "' + topicId + '" has invalid grouper data (expected array)'
  );
  return grouperData;
}

export function ensureGrouperRows(
  grouperRows: any[],
  grouperStructure: any,
  topicId: string
): any[] {
  ensureGrouperStructureExists(grouperStructure, topicId);
  const rows = ensureGrouperArray(grouperRows, topicId);

  const levelDefinitions = Array.isArray(grouperStructure?.levels)
    ? grouperStructure.levels
    : [];
  const allowedLevelIds = new Set(
    levelDefinitions.map((level: any, index: number) => normalizeLevelId(level, index))
  );

  rows.forEach((row: any, index: number) => {
    assertCondition(
      row && typeof row === 'object',
      `Topic "${topicId}" has invalid grouper row at index ${index}`
    );

    const grouperId = row?.grouperId;
    assertCondition(
      typeof grouperId === 'string' && grouperId.trim().length > 0,
      `Topic "${topicId}" grouper row ${index + 1} missing grouperId`
    );

    const numericOrder =
      typeof row?.order === 'number' && !Number.isNaN(row.order)
        ? row.order
        : parseInt(String(row?.order ?? ''), 10);
    assertCondition(
      Number.isInteger(numericOrder) && numericOrder >= 1,
      `Topic "${topicId}" grouper row "${grouperId}" has invalid order position`
    );
    row.order = numericOrder;

    assertCondition(
      Array.isArray(row.levels),
      `Topic "${topicId}" grouper row "${grouperId}" missing levels array`
    );

    row.levels.forEach((levelEntry: any) => {
      const identifier = String(levelEntry?.level ?? levelEntry?.number ?? '').toLowerCase();
      if (identifier === 'custom') {
        assertCondition(
          typeof levelEntry.value === 'string' || levelEntry.value === undefined,
          `Topic "${topicId}" grouper row "${grouperId}" custom level must use string value`
        );
        return;
      }

      const normalizedId = String(levelEntry?.level ?? levelEntry?.number ?? '');
      assertCondition(
        allowedLevelIds.has(normalizedId),
        `Topic "${topicId}" grouper row "${grouperId}" contains unexpected level "${normalizedId}"`
      );
    });

    levelDefinitions.forEach((levelDef: any, levelIndex: number) => {
      const levelId = normalizeLevelId(levelDef, levelIndex);
      const referenceSources = getReferenceSources(levelDef);
      const entry = row.levels.find(
        (lvl: any) => String(lvl?.level ?? '') === levelId
      );

      assertCondition(
        entry && Array.isArray(entry.refs),
        `Topic "${topicId}" grouper row "${grouperId}" level "${levelId}" missing refs array`
      );

      assertCondition(
        entry.refs.length === referenceSources.length,
        `Topic "${topicId}" grouper row "${grouperId}" level "${levelId}" expected ${referenceSources.length} refs but received ${entry.refs.length}`
      );

      entry.refs = entry.refs.map((ref: any) =>
        typeof ref === 'string' ? ref : ''
      );
    });
  });

  return rows;
}

export function createEmptyGrouperRow(
  grouperStructure: any,
  grouperId: string | number,
  order: number
): any {
  ensureGrouperStructureExists(grouperStructure, 'createEmptyGrouperRow');

  const levels = (grouperStructure.levels || []).map((level: any, index: number) => {
    const levelId = normalizeLevelId(level, index);
    const referenceSources = getReferenceSources(level);
    return {
      level: levelId,
      refs: referenceSources.map(() => '')
    };
  });

  return {
    grouperId: String(grouperId),
    order: Number.isInteger(order) && order > 0 ? order : 1,
    levels,
    cells: [] // Initialize empty cells array for grouper column values
  };
}